import { Text, View } from "@react-pdf/renderer";
import ContractStyles from "../../assets/styles/contractStyles";
import ContractListItem from "../../constants/listItem";
import RenderClauses from "../../constants/renderClauses";
import {
  formatSerialNumber,
  numberClauseTree,
} from "../../constants/serialNumbers";

const SECTION_DATA = [
  {
    title: "Defined Terms",
    introduction:
      "Except where the context requires otherwise, the following terms used in this agreement bear the following meanings:",
    type: "definitions",
  },
  {
    title: "Appointment & Scope",
    clauses: [
      {
        content:
          "With effect form the Commencement Date, Rosemount appoints the Adviser to carry out:",
        subContent: [
          {
            content:
              "Investment Arrangements: Making arrangements for clients to enter into, vary, or carry out investment transactions with authorised counterparties, and providing advice where permitted under the Regulated Activities Order.",
          },
          {
            content:
              "Insurance-Related Activities: Arranging, assisting in the administration, and performance of insurance contracts, and providing related advice.",
          },
          {
            content:
              "Home Finance Transactions: Arranging or advising on regulated mortgage contracts, buy-to-let lending (whether regulated or unregulated), and other home finance transactions for clients, including advice on variations, in accordance with applicable law, FCA rules (where applicable), and Network procedures.",
          },
          {
            content:
              "Equity Release Activities: Advising on and arranging equity release products for customers, ensuring that such activities comply with applicable regulations and are conducted in a manner that prioritises the best interests of clients.",
          },
          {
            content:
              "Long-Term Care Planning: Advising on and arranging regulated financial products designed to fund long-term care needs, including insurance policies and investment solutions, in accordance with FCA rules and the Consumer Duty.",
          },
        ],
      },
      {
        content:
          "During the course of the appointment, the Adviser shall not, carry out any regulated activities or provide advice in relation to services except as authorised under this agreement, and shall not encourage or advise any person to take up services outside of Rosemount.",
      },
      {
        content:
          "The Adviser shall only carry out regulated activities for which they are qualified and authorised for within the scope of activities permitted by the Network.",
      },
    ],
  },
  {
    title: "Undertakings by the Adviser",
    clauses: [
      {
        content:
          "The Adviser shall at all times during the course of the Appointment:",
        subContent: [
          {
            content:
              "Comply with all reasonable requests of the Appointed Representative and the Network, including attending training, supervision meetings, and competency assessments.",
          },
          {
            content:
              "The Adviser shall at all times comply with all applicable regulatory obligations, including the Financial Services and Markets Act 2000, FCA rules and guidance, and the Network’s compliance procedures.",
          },
          {
            content:
              "The Adviser must not provide advice or carry out regulated activities through, or on behalf of, any party other than Rosemount or the Appointed Representative without Rosemount’s prior written consent.",
          },
          {
            content:
              "Complete all mandatory Continuing Professional Development (CPD) and refresher training in accordance with the Network’s standards and FCA requirements.",
          },
          {
            content:
              "If self-employed, hold and maintain a valid Data Protection Licence (DPL) as required by the Information Commissioner’s Office (ICO), and comply at all times with applicable data protection laws.",
          },
          {
            content:
              "Adhere to the Network’s Training & Competence (T&C) scheme, including completion of assessments, supervisory reviews, and any remedial actions as directed by the Network or Appointed Representative.",
          },
          {
            content:
              "Immediately report to the Network and Appointed Representative any breach of FCA rules, complaints, conflicts of interest, or regulatory investigations.",
          },
          {
            content:
              "No financial promotion may be published or distributed without the prior written approval of the Network. Any breach may result in immediate suspension or termination of this agreement.",
          },
          {
            content:
              "The Adviser shall not accept business from any third-party introducers without the prior written approval of the Network and shall ensure any approved introducers comply with FCA rules and Network procedures.",
          },
          {
            content:
              "The Adviser shall at all times act in the best interests of clients, deliver good outcomes, and comply with the FCA Consumer Duty principle. All advice, recommendations, financial promotions, and actions must consider foreseeable harm, be suitable for the client, and comply with the Network’s procedures and supervision requirements.",
          },
          {
            content:
              "The Adviser shall promptly notify the Network and Appointed Representative of any change in qualifications, fitness and propriety, personal financial standing, or any other matter that may affect their ability to carry out regulated activities safely and compliantly.",
          },
        ],
      },
    ],
  },
  {
    title: "Suspension",
    clauses: [
      {
        content:
          "Rosemount may, at its sole discretion and by written notice with immediate effect, suspend the Adviser’s permission to carry out all or part of the activities under this agreement for such period as it considers necessary, including for regulatory, compliance, or performance reasons.",
      },
      {
        content:
          "During a period of suspension, the Adviser shall remain an appointed representative of Rosemount but shall not carry out any suspended activities, except as specifically authorised in writing by Rosemount.",
      },
    ],
  },
  {
    title: "Termination",
    clauses: [
      {
        content:
          "Rosemount may terminate this agreement at any time by notice in writing with immediate effect if any one or more of the following occurs:",
        subContent: [
          {
            content:
              "The termination of the contract between the Adviser and the Appointed Representative.",
          },
          {
            content: "The Adviser becomes subject to a bankruptcy order of any kind.",
          },
          {
            content:
              "Persistent or material breach of the obligations under this agreement, including material non-compliance with FCA rules, Network procedures, Consumer Duty obligations, financial promotion rules, or conflicts of interest.",
          },
        ],
      },
      {
        content:
          "If termination or suspension occurs due to a regulatory breach, Rosemount may notify the FCA as required by law or regulation.",
      },
      {
        content:
          "The Adviser shall immediately return all property, documents, and materials belonging to Rosemount or the Appointed Representative.",
      },
      {
        content:
          "Any client data relating to clients introduced by, or generated through, Rosemount or the Appointed Representative during the term of this Agreement shall remain the property of Rosemount and the Appointed Representative. The Adviser shall have no right to access, store, or transfer such data except as necessary for the performance of their duties under this Agreement, or as otherwise agreed in writing by Rosemount or the Appointed Representative. For the avoidance of doubt, unless agreed otherwise, this clause does not apply to clients independently generated by the Adviser outside of Rosemount or the Appointed Representative.",
      },
    ],
  },
  {
    title: "Variation",
    clauses: [
      {
        content:
          "It is hereby agreed between the parties that Rosemount may vary the terms of this agreement by serving 30 days' written notice to the Adviser.",
      },
    ],
  },
  {
    title: "Jurisdiction",
    clauses: [
      {
        content:
          "This agreement shall be governed by and construed in accordance with the laws of England and the parties irrevocably submit to the exclusive jurisdiction of the English courts to settle any disputes which may arise in connection with this agreement.",
      },
    ],
  },
];

const DefinitionRow = ({ label, children, styles }) => (
  <View style={[styles.flexRow, styles.alignStart]} wrap={false}>
    <Text style={[styles.buttonTextSmall, styles.text_primary, { width: 150 }]}>
      {label}
    </Text>
    <Text style={[styles.buttonTextSmall, styles.text_red, styles.grow]}>
      {children}
    </Text>
  </View>
);

const RIContractContent = ({ contractDetails = {} }) => {
  const styles = ContractStyles();
  const adviserName = contractDetails.applicantName || "Insert Adviser's full name";
  const appointedRepresentativeName =
    contractDetails.organisationName || "Insert AR's full name";

  return (
    <View style={[styles.flexColumn, styles.gap_md]}>
      <Text style={[styles.contentSemibold, styles.text_primary]}>
        Adviser Agreement
      </Text>

      <View style={[styles.flexColumn, styles.gap_sm]}>
        <Text style={[styles.contentSemibold, styles.text_primary]}>
          PARTIES:
        </Text>
        <Text style={styles.buttonTextSmall}>
          This agreement is made between the ‘Adviser’ and Rosemount Financial
          Solutions (IFA) Limited (Company Registration Number 05668905) whose
          registered office is at Rosemount House, 2-4 Chequers Road,
          Basingstoke, United Kingdom, RG21 7PU (the “Network”) and will be
          referred to in this agreement as ‘Rosemount’.
        </Text>
      </View>

      {SECTION_DATA.map(
        ({ title, introduction, clauses, type }, sectionIndex) => {
          const serialNumber = sectionIndex + 1;

          return (
          <View
            key={title}
            id={`ri-section-${serialNumber}`}
            style={[styles.flexColumn, styles.gap_sm]}
            break={serialNumber === 6}
          >
              <ContractListItem
                marker={formatSerialNumber(serialNumber)}
                styles={styles}
                textStyle={[styles.contentSemibold, styles.text_primary]}
                wrap={false}
              >
                {title}
              </ContractListItem>

              {introduction ? (
                <Text style={styles.buttonTextSmall}>{introduction}</Text>
              ) : null}

              {type === "definitions" ? (
                <View
                  style={[
                    styles.flexColumn,
                    styles.gap_xs,
                    { paddingLeft: 36 },
                  ]}
                >
                  <DefinitionRow label="Adviser" styles={styles}>
                    Insert Advisers full name
                  </DefinitionRow>
                  <DefinitionRow
                    label="Appointed Representative"
                    styles={styles}
                  >
                    Insert ARs full name
                  </DefinitionRow>
                  <DefinitionRow label="Network" styles={styles}>
                    Rosemount Financial Solutions (IFA) Ltd
                  </DefinitionRow>
                </View>
              ) : (
                <RenderClauses
                  clauses={numberClauseTree(clauses, serialNumber)}
                  styles={styles}
                  number={serialNumber}
                />
              )}
            </View>
          );
        },
      )}

      <View style={[styles.flexColumn, styles.gap_sm]}>
        <Text style={[styles.contentSemibold, styles.text_primary]}>
          Declaration
        </Text>
        <Text style={styles.buttonTextSmall}>
          The Adviser hereby declares and warrants that all information provided
          by him in connection with his Appointment is true, complete and
          accurate in all material respects.
        </Text>

        <View
          style={[styles.flexColumn, styles.gap_md, { marginTop: 16 }]}
          wrap={false}
        >
          <Text style={styles.buttonTextSmall}>
            Signed by <Text style={styles.text_red}>insert adviser name</Text>{" "}
            (the “Adviser”)
          </Text>
          <Text style={styles.buttonTextSmall}>
            _____________________________
          </Text>
          <Text style={styles.buttonTextSmall}>
            Date: _______________________
          </Text>
        </View>

        <View
          style={[styles.flexColumn, styles.gap_md, { marginTop: 16 }]}
          wrap={false}
        >
          <Text style={styles.buttonTextSmall}>
            Signed by Rosemount Financial Solutions (IFA) Ltd (the “Network”)
          </Text>
          <Text style={styles.buttonTextSmall}>
            _____________________________
          </Text>
          <Text style={styles.buttonTextSmall}>
            Date: _______________________
          </Text>
        </View>
      </View>
    </View>
  );
};

export default RIContractContent;
